import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';
import { Transaction } from '../_models/transaction';
import { CashPositions } from '../_models/cash-positions';
import { Equity } from '../_models/equity';
import { CurrentPortfolio } from '../_models/current-portfolio';
import { AccountInfo } from '../_models/account-info';
import { CorporateActions } from '../_models/corporate-actions';
import * as moment from 'moment';
import { Benchmark } from '../_models/benchmark';
import { TaxTransaction } from '../_models/tax-transaction';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class HttpRequestService {

  private readonly MAX_CASH_POSITION = 0.01;
  private url = environment.backendEndpoint;

  private readonly fetchTransactionsUrl = this.url + 'fetchTransactions/';
  private readonly fetchTaxTransactionsUrl = this.url + 'fetchTaxTransactions/';
  private readonly fetchCashPositionsUrl = this.url + 'fetchCashPositions/';
  private readonly fetchEquityUrl = this.url + 'fetchEquity/';
  private readonly fetchCurrentPortfolioUrl = this.url + 'fetchCurrentPortfolio/';
  private readonly fetchAccountInfoUrl = this.url + 'fetchAccountInfo/';
  private readonly fetchCorporateActionsUrl = this.url + 'fetchCorporateActions/';
  private readonly fetchBenchmarksUrl = this.url + 'fetchBenchmarks/';
  private readonly productsIds = {
    'UIM1': '14711488', //UBS ETF EURO STOXX (ESTX50)
    'IBXLQ': '31004874', //ISHARES EURO CORP large corp bonds
    'IBGS': '39992901', //ISHARES EUR GOVT 1-3 year bonds
    'BTMA': '100292090', //ISHARES USD TREASURY 7-10 years bonds
    'VFEM': '128831223', //VANGUARD FTSE Emerging markets
    'SPXS': '166084812', //INVESCO (S&P 500)
    'UIMY-NA': '207609361' //UBS ETF Low volatility
  };

  public transactionsData = new Subject<Transaction[]>();
  public taxTransactionsData = new Subject<TaxTransaction[]>();
  public equityData = new Subject<Equity[]>();
  public currentPortfolioData = new Subject<CurrentPortfolio[]>();
  public cashPositionsData = new Subject<any>();
  public accountInfoData = new Subject<AccountInfo>();
  public corporateActionsData = new Subject<CorporateActions[]>();
  public benchmarkData = new Subject<Benchmark[]>();

  constructor(private http: HttpClient, private route: ActivatedRoute) { }

  public fetchTransactions() {
    this.createHttpRequest(this.getTimeframe(), this.fetchTransactionsUrl).subscribe(responseData => {
      this.transactionsData.next(responseData);
    });
  }

  public fetchTaxTransactions(descriptions: string[]) {
    this.createHttpRequestForTaxTransactions(this.getTimeframe(), descriptions).subscribe(responseData => {
      this.taxTransactionsData.next(responseData);
    });
  }

  public fetchCashPositions() {
    // This also does weekend check
    let timeframe = this.getTimeframe();
    timeframe = this.modifyIfJanuaryFirst(timeframe);

    this.createHttpRequest(timeframe, this.fetchCashPositionsUrl).subscribe(responseData => {
      responseData = responseData.filter(cashPosition => cashPosition != null);
      if (responseData.length === 0) {
        this.cashPositionsData.next({});
        return
      }
      let startCashPositions: CashPositions[] = [];
      let endCashPositions: CashPositions[] = [];
      let dayOrMonthToSplitOn = this.getSplitCondition(timeframe);
      for (let cashPosition of responseData) {
        let currentDayOrMonth = this.getCurrentDayOrMonth(timeframe, cashPosition);
        if (Math.abs(cashPosition.value) >= this.MAX_CASH_POSITION) currentDayOrMonth === dayOrMonthToSplitOn ? startCashPositions.push(cashPosition) : endCashPositions.push(cashPosition);
      }
      this.normalizeCashPositions(responseData, startCashPositions, endCashPositions);
      this.cashPositionsData.next({
        initialCashPositions: responseData, 
        startCashPositions: startCashPositions,
        endCashPositions: endCashPositions
      });
    });
  }

  public fetchEquity() {
    let timeframe = this.getTimeframe();
    timeframe = this.modifyIfJanuaryFirst(timeframe);

    this.createHttpRequest(timeframe, this.fetchEquityUrl).subscribe(responseData => {
      responseData = this.parseEquityDates(responseData);
      this.equityData.next(responseData);
    });
  }

  private parseEquityDates(equity: Equity[]): Equity[] {
    equity.forEach(day => {
      day.date = day.date instanceof Object ? moment(new Date(day.date)).format('YYYY-MM-DD') : day.date;
      day.transfers.forEach(transfer => transfer.date = transfer.date instanceof Object ? moment(new Date(transfer.date)).format('YYYY-MM-DD') : transfer.date);
    });
    return equity;
  }

  public fetchCurrentPortfolio() {
    let timeframe = this.getTimeframe();
    timeframe = this.modifyIfJanuaryFirst(timeframe);

    this.createHttpRequest(timeframe, this.fetchCurrentPortfolioUrl).subscribe(responseData => {
      this.currentPortfolioData.next(responseData);
    })
  }

  public fetchAccountInfo() {
    let url = this.fetchAccountInfoUrl;
    let accountNumber = this.route.snapshot.queryParams.accountNumber;
    if (accountNumber) {
      url += accountNumber;
    }
    this.http.get<AccountInfo>(url).subscribe(responseData => {
      this.accountInfoData.next(responseData);
    })
  }

  public fetchCorporateActions() {
    this.createHttpRequest(this.getTimeframe(), this.fetchCorporateActionsUrl).subscribe(responseData => {
      this.corporateActionsData.next(responseData);
    })
  }

  public fetchBenchmarksData() {
    const timeframe = this.getTimeframe();
    this.http.get<Benchmark[]>(this.fetchBenchmarksUrl + Object.values(this.productsIds) + '?dateRange=' + timeframe.startDate.format('DD-MM-YYYY') + 'to' + timeframe.endDate.format('DD-MM-YYYY')).subscribe(responseData => {
      this.benchmarkData.next(responseData);
    })
  }


  private normalizeCashPositions(initialCashPositions, startCashPositions, endCashPositions) {
    let nonZeroInitialCashPositions = initialCashPositions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).filter(x => Math.abs(x.value) >= this.MAX_CASH_POSITION);
    this.fillInBlanks(nonZeroInitialCashPositions, startCashPositions, endCashPositions);
  }

  private fillInBlanks(nonZeroInitialCashPositions: any, startCashPositions: any, endCashPositions: any) {
    nonZeroInitialCashPositions.forEach(el1 => {
      let doesCurrencyHavePair = false;
      nonZeroInitialCashPositions.forEach(el2 => {
        if (el1.currencyOriginal === el2.currencyOriginal && el1.date !== el2.date) {
          doesCurrencyHavePair = true;
          return;
        }
      });
      if (!doesCurrencyHavePair) {
        this.addDummyValue(startCashPositions, el1, endCashPositions);
      }
    });
    this.sortBasedOnCurrency(startCashPositions, endCashPositions);
  }

  private sortBasedOnCurrency(startCashPositions: any, endCashPositions: any) {
    startCashPositions.sort((a, b) => (a.currencyOriginal > b.currencyOriginal ? -1 : 1));
    endCashPositions.sort((a, b) => (a.currencyOriginal > b.currencyOriginal ? -1 : 1));
  }

  private addDummyValue(startCashPositions: any, el1: any, endCashPositions: any) {
    if (startCashPositions.includes(el1)) {
      endCashPositions.push({ id: null, account: el1.account, date: endCashPositions.length > 0 ? endCashPositions[0].date : "", valueOriginal: 0, value: 0, currencyOriginal: el1.currencyOriginal, currency: el1.currency });
    }
    else {
      startCashPositions.push({ id: null, account: el1.account, date: startCashPositions.length > 0 ? startCashPositions[0].date : "", value: 0, valueOriginal: 0, currency: el1.currency, currencyOriginal: el1.currencyOriginal });
    }
  }

  private createHttpRequest(timeframe: any, givenUrl: string) {
    let accountNumber = this.route.snapshot.queryParams.accountNumber;
    if (accountNumber) {
      givenUrl += accountNumber;
    }
    return this.http.get<any[]>(`${givenUrl}?dateRange=${timeframe.startDate.format('DD-MM-YYYY')}to${timeframe.endDate.format('DD-MM-YYYY')}`);
  }

  private createHttpRequestForTaxTransactions(timeframe: any, descriptions: string[]) {
    let url = this.fetchTaxTransactionsUrl;
    let accountNumber = this.route.snapshot.queryParams.accountNumber;
    if (accountNumber) {
      url += accountNumber;
    }
    return this.http.get<any[]>(`${url}?dateRange=${timeframe.startDate.format('DD-MM-YYYY')}to${timeframe.endDate.format('DD-MM-YYYY')}&descriptions=${descriptions}`);
  }

  changePassword(data: any, category : string): Observable<any> {
    return this.http.put<any>(`${this.url}portal/user/password?name=${category}`, data);
  }

  getSplitCondition(timeframe: { startDate: moment.Moment; endDate: moment.Moment; }): string {
    if (this.areDatesWithinSameMonthAndYear(timeframe.startDate, timeframe.endDate)) {
      return timeframe.startDate.format('D');
    } else {
      return timeframe.startDate.format('M') + timeframe.startDate.format('Y');
    }
  }

  private getCurrentDayOrMonth(timeframe: { startDate: moment.Moment; endDate: moment.Moment; }, cashPosition: any) {
    let givenDate = new Date(cashPosition.date);
    let startDateRange = timeframe.startDate;
    let endDateRange = timeframe.endDate;
    return this.areDatesWithinSameMonthAndYear(startDateRange, endDateRange) ? givenDate.getDate().toString() : (givenDate.getMonth() + 1).toString() + givenDate.getFullYear();
  }

  private areDatesWithinSameMonthAndYear(firstDate: moment.Moment, secondDate: moment.Moment) {
    return firstDate.format('M') === secondDate.format('M') && firstDate.format('Y') === secondDate.format('Y');
  }

  checkTimeFrameValidity(timeframe: string): boolean {
    if (timeframe.startsWith('Q')) {
      const regex = /^Q[1-4]\s20(1[7-9]|2[0-9])$/;
      return regex.test(timeframe);
    } else {
      const dateRange = [moment(timeframe.split("to")[0], 'DD-MM-YYYY'), moment(timeframe.split("to")[1], 'DD-MM-YYYY')];
      if (dateRange[1].isAfter(dateRange[0]) && dateRange[0].year() > 2017 && moment().subtract(2, 'days').isAfter(dateRange[1])) {
        if (moment(timeframe.split("to")[0], 'DD-MM-YYYY').isValid() && moment(timeframe.split("to")[1], 'DD-MM-YYYY').isValid()) {
          return true;
        }
      }
      return false;
    }
  }

  public getTimeframe(): { startDate: moment.Moment, endDate: moment.Moment } {
    const dateRange = this.route.snapshot.queryParams.dateRange;
    const quarter = this.route.snapshot.queryParams.quarter;
    let startDate: moment.Moment;
    let endDate: moment.Moment;
    
    if (dateRange) {
      startDate = moment(dateRange.split('to')[0], 'DD/MM/YYYY');
      endDate = moment(dateRange.split('to')[1], 'DD/MM/YYYY');
    } else if (quarter) {
      startDate = moment().year(quarter.substring(3, 7)).quarter(quarter.charAt(1)).startOf('quarter');
      endDate = moment().year(quarter.substring(3, 7)).quarter(quarter.charAt(1)).endOf('quarter');
    }
    let dateNotReachedYet = moment().diff(endDate) < 0;
    if (dateNotReachedYet) {
      endDate = moment().subtract(2, 'day');
    }
    return this.modifyIfWeekend(startDate, endDate);
  }

  public modifyIfJanuaryFirst(timeframe: { startDate: moment.Moment; endDate: moment.Moment; }) {
    if (timeframe.startDate.date() === 1 && timeframe.startDate.month() === 0) {
      timeframe.startDate = timeframe.startDate.subtract(1, 'days');
    }
    if (timeframe.endDate.date() === 1 && timeframe.endDate.month() === 0) {
      timeframe.endDate = timeframe.endDate.subtract(1, 'days');
    }
    return timeframe;
  }

  public modifyIfWeekend(startDate: moment.Moment, endDate: moment.Moment): { startDate: moment.Moment, endDate: moment.Moment } {
    if (startDate.day() === 6) {
      startDate = startDate.subtract(1, 'days');
    } else if (startDate.day() === 0) {
      startDate = startDate.subtract(2, 'days');
    }

    if (endDate.day() === 6) {
      endDate = endDate.subtract(1, 'days');
    } else if (endDate.day() === 0) {
      endDate = endDate.subtract(2, 'days');
    }
    return { startDate: startDate, endDate: endDate };
  }

  public resetValues() {
    this.currentPortfolioData.next([]);
    this.transactionsData.next([]);
    this.taxTransactionsData.next([]);
    this.equityData.next([]);
    this.currentPortfolioData.next([]);
    this.cashPositionsData.next([]);
    this.accountInfoData.next();
    this.corporateActionsData.next([]);
    this.benchmarkData.next([]);
  }

  public destroy() {
    this.currentPortfolioData = new Subject<CurrentPortfolio[]>();
    this.transactionsData = new Subject<Transaction[]>();
    this.taxTransactionsData = new Subject<TaxTransaction[]>();
    this.equityData = new Subject<Equity[]>();
    this.currentPortfolioData = new Subject<CurrentPortfolio[]>();
    this.cashPositionsData = new Subject<any>();
    this.accountInfoData = new Subject<AccountInfo>();
    this.corporateActionsData = new Subject<CorporateActions[]>();
    this.benchmarkData = new Subject<Benchmark[]>();
  }

}
